"use client";

import React, {
  useContext, useEffect, useMemo, useState,
} from "react";

import config from "../services/config";
import { createNotificationPushSubscription } from "../services/api";

import { useAuth } from "./AuthContext";

interface Props {
  children: React.ReactNode;
}

interface ServiceWorkerContextType {
  // eslint-disable-next-line no-undef
  pushSubscription?: PushSubscription;
  registerPush: () => Promise<void>;
  unregisterPush: () => Promise<void>;
  loadingPush: boolean;
  pushSupported: boolean;
}

const ServiceWorkerContext = React.createContext<ServiceWorkerContextType>({
  registerPush: () => Promise.resolve(),
  unregisterPush: () => Promise.resolve(),
  loadingPush: false,
  pushSupported: false,
});

const ServiceWorkerProvider: React.FC<Props> = ({ children }) => {
  const [sw, setSW] = useState<ServiceWorkerRegistration>();
  const [loadingPush, setLoadingPush] = useState<boolean>(true);
  const { user } = useAuth();
  const [pushSupported, setPushSupported] = useState<boolean>(false);

  // eslint-disable-next-line no-undef
  const [pushSubscription, setPushSubscription] = useState<PushSubscription>();

  useEffect(() => {
    initSW();
  }, []);

  const initSW = async () => {
    try {
      if (!("serviceWorker" in navigator) || !("Notification" in window)) {
        setPushSupported(false);
      } else {
        setLoadingPush(true);
        const sw = await navigator.serviceWorker.register("/service-worker.js");
        setSW(sw);
        const sub = await sw.pushManager.getSubscription();
        setPushSubscription(sub);
        setPushSupported(true);
      }
    } catch (err) {
      console.error("Error initiating push service worker:", err);
    } finally {
      setLoadingPush(false);
    }
  };

  useEffect(() => {
    if (!pushSubscription || !user) return;
    createNotificationPushSubscription({
      variables: {
        subscription: JSON.stringify(pushSubscription),
      },
    });
  }, [pushSubscription, user]);

  const registerPush = async () => {
    try {
      if (!pushSupported) return;
      const result = await Notification.requestPermission();
      if (result === "denied") return;
      const subscribeOptions = {
        userVisibleOnly: true,
        applicationServerKey: config.vapidPublicKey,
      };
      const sub = await sw.pushManager.subscribe(subscribeOptions);
      setPushSubscription(sub);
    } catch (err) {
      console.error("Error subscribing to push notifications:", err);
    }
  };

  const unregisterPush = async () => {
    try {
      if (pushSubscription) {
        await pushSubscription.unsubscribe();
        setPushSubscription(undefined);
      }
    } catch (err) {
      console.error("Error unsubscribing from push notifications:", err);
    }
  };

  const value = useMemo(() => ({
    pushSubscription, registerPush, unregisterPush, loadingPush, pushSupported,
  }), [pushSubscription, registerPush, unregisterPush, loadingPush, pushSupported]);

  return (
    <ServiceWorkerContext.Provider value={value}>
      {children}
    </ServiceWorkerContext.Provider>
  );
};

const useServiceWorker = () => useContext(ServiceWorkerContext);

export default ServiceWorkerContext;
export { ServiceWorkerProvider, useServiceWorker };
